import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { SIZE } from '../../../../../../../../constants/size'
import { Divider } from '../../../../../../../Divider'
import { FormBody, FormFieldsContainer } from '../../../../../../../Form'
import { Content } from '../../../../../../../Modal'
import { NotificationInlineList } from '../../../../../../../Notification'
import Text from '../../../../../../../Text'
import {
  MATCH_RULES_COLUMN_TITLE_KEYS,
  MATCH_RULES_FIELD_NAME_BUDGET_GATE,
  MATCH_RULES_FIELD_NAME_CRITERIA,
  MATCH_RULES_FIELD_NAME_GATING,
  MATCH_RULES_FIELD_NAME_WEIGHTS,
} from '../../ListingProfileApplicantsMatchRules.constants'
import {
  getIsMatchRulesBudgetCriterion,
  getMatchRulesGatingCriteria,
  getMatchRulesNotifications,
  getMatchRulesWeightHint,
} from '../../ListingProfileApplicantsMatchRules.utils'
import { useMatchRulesImpactPreview } from '../ListingProfileApplicantsMatchRulesModal.hooks'
import {
  getHasMatchRulesGatingValue,
  getMatchRulesAddOptions,
  getMatchRulesScoredRows,
} from '../ListingProfileApplicantsMatchRulesModal.utils'
import { ListingProfileApplicantsMatchRulesModalAddCriterion } from '../ListingProfileApplicantsMatchRulesModalAddCriterion'
import { ListingProfileApplicantsMatchRulesModalBudgetGate } from '../ListingProfileApplicantsMatchRulesModalBudgetGate'
import { ListingProfileApplicantsMatchRulesModalGatingValue } from '../ListingProfileApplicantsMatchRulesModalGatingValue'
import { ListingProfileApplicantsMatchRulesModalRow } from '../ListingProfileApplicantsMatchRulesModalRow'

import * as styles from './ListingProfileApplicantsMatchRulesModalFields.module.css'

function ListingProfileApplicantsMatchRulesModalFields({ rules, onCancel }) {
  const { t } = useTranslation()

  const { initialValues, resetForm, setFieldValue, values } = useFormikContext()

  const isReadOnly = !rules.can_configure_matching

  const scoredKeys = values[MATCH_RULES_FIELD_NAME_CRITERIA]

  const weights = values[MATCH_RULES_FIELD_NAME_WEIGHTS]

  const gating = values[MATCH_RULES_FIELD_NAME_GATING]

  const { impact } = useMatchRulesImpactPreview({
    criteria: scoredKeys,
    rules,
  })

  function handleChangeWeight(key, weight) {
    setFieldValue(MATCH_RULES_FIELD_NAME_WEIGHTS, { ...weights, [key]: weight })
  }

  function handleChangeGating(next) {
    setFieldValue(MATCH_RULES_FIELD_NAME_GATING, next)
  }

  function handleChangeBudgetGate(isGate) {
    setFieldValue(MATCH_RULES_FIELD_NAME_BUDGET_GATE, isGate)
  }

  function handleAdd(option) {
    setFieldValue(MATCH_RULES_FIELD_NAME_CRITERIA, [...scoredKeys, option.key])
    setFieldValue(MATCH_RULES_FIELD_NAME_WEIGHTS, {
      ...weights,
      [option.key]: option.default_weight,
    })
  }

  function handleRemove(key) {
    setFieldValue(
      MATCH_RULES_FIELD_NAME_CRITERIA,
      scoredKeys.filter(item => item !== key)
    )
  }

  function handleReset() {
    resetForm()
  }

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: isReadOnly ? false : undefined,
        ButtonSubmit: isReadOnly ? false : undefined,
        UploadComponent: false,
        cancelProps: {
          title: isReadOnly ? 'RentalPortal.MatchRulesCloseBtnKey' : undefined,
          onClick: onCancel,
        },
        className: styles.actions,
        clearProps: {
          title: 'RentalPortal.MatchRulesResetBtnKey',
          onClick: handleReset,
        },
        submitProps: {
          title: t('RentalPortal.MatchRulesApplyBtnKey', {
            count: rules.affected_applicants_count,
          }),
        },
      }}
    >
      <Content size={SIZE.X_LARGE}>
        <FormFieldsContainer>
          {isReadOnly ? (
            <Text isSecondary>{t('RentalPortal.MatchRulesReadOnlyKey')}</Text>
          ) : null}
          <NotificationInlineList
            className={styles.notifications}
            items={getMatchRulesNotifications({
              impact,
              initialValues,
              t,
              values,
            })}
          />
          <div className={styles.table}>
            {MATCH_RULES_COLUMN_TITLE_KEYS.map(titleKey => (
              <Text isSecondary className={styles.columnTitle} key={titleKey}>
                {t(titleKey)}
              </Text>
            ))}
            <div className={styles.columnTitle} />
            <Divider className={styles.divider} />
            <div className={styles.group}>
              <Text isSecondary className={styles.groupTitle}>
                {t('RentalPortal.MatchRulesGroupGatingKey')}
              </Text>
            </div>
            {getMatchRulesGatingCriteria(rules).map(criterion => (
              <ListingProfileApplicantsMatchRulesModalRow
                criterion={criterion}
                hasWeight={criterion.weight > 0}
                isReadOnly={isReadOnly}
                key={criterion.key}
                value={
                  getHasMatchRulesGatingValue(criterion) ? (
                    <ListingProfileApplicantsMatchRulesModalGatingValue
                      criterionKey={criterion.key}
                      isReadOnly={isReadOnly}
                      values={gating}
                      onChange={handleChangeGating}
                    />
                  ) : null
                }
                weight={weights[criterion.key]}
                weightHint={getMatchRulesWeightHint({ criterion, rules, t })}
                onChangeWeight={handleChangeWeight}
              />
            ))}
            <div className={styles.group}>
              <Text isSecondary className={styles.groupTitle}>
                {t('RentalPortal.MatchRulesGroupScoredKey')}
              </Text>
              <ListingProfileApplicantsMatchRulesModalAddCriterion
                isReadOnly={isReadOnly}
                options={getMatchRulesAddOptions({
                  criteria: scoredKeys,
                  rules,
                })}
                onAdd={handleAdd}
              />
            </div>
            {getMatchRulesScoredRows({ criteria: scoredKeys, rules }).map(
              criterion => (
                <ListingProfileApplicantsMatchRulesModalRow
                  hasRemove
                  hasWeight
                  criterion={criterion}
                  isReadOnly={isReadOnly}
                  key={criterion.key}
                  value={
                    getIsMatchRulesBudgetCriterion(criterion) ? (
                      <ListingProfileApplicantsMatchRulesModalBudgetGate
                        criterion={criterion}
                        isGate={values[MATCH_RULES_FIELD_NAME_BUDGET_GATE]}
                        isReadOnly={isReadOnly}
                        onChange={handleChangeBudgetGate}
                      />
                    ) : null
                  }
                  weight={weights[criterion.key]}
                  weightHint={getMatchRulesWeightHint({
                    criterion,
                    rules,
                    t,
                  })}
                  onChangeWeight={handleChangeWeight}
                  onRemove={handleRemove}
                />
              )
            )}
          </div>
        </FormFieldsContainer>
      </Content>
    </FormBody>
  )
}

export default ListingProfileApplicantsMatchRulesModalFields
